import React, { useState } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Home, Target, Bell, User, ArrowLeft, Store, Trophy, LayoutDashboard, TrendingUp } from 'lucide-react';
import NotificationSheet from './NotificationSheet';

export default function BottomNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [showNotifications, setShowNotifications] = useState(false);

  if (!user) return null;

  const path = location.pathname;
  const isInClass = path.startsWith('/class/');
  const isAdmin = user.role === 'admin';
  const isProfessor = user.role === 'professor';

  // Context-aware items
  let items;
  if (isAdmin) {
    items = [
      { icon: Home, label: 'Inicio', to: '/admin', action: 'navigate' },
      { icon: Bell, label: 'Alertas', action: 'notifications' },
      { icon: User, label: 'Perfil', to: '/settings', action: 'navigate' },
    ];
  } else if (isInClass) {
    // Inside a class - show relevant nav
    items = [
      { icon: ArrowLeft, label: 'Volver', to: '/dashboard', action: 'navigate' },
      { icon: isProfessor ? TrendingUp : LayoutDashboard, label: isProfessor ? 'Resumen' : 'Progreso', action: 'tab', tab: isProfessor ? 'overview' : 'dashboard' },
      { icon: Target, label: 'Actividades', action: 'tab', tab: 'activities' },
      { icon: Store, label: 'Tienda', action: 'tab', tab: 'marketplace' },
      { icon: Trophy, label: 'Ranking', action: 'tab', tab: 'leaderboard' },
    ];
  } else {
    items = [
      { icon: Home, label: 'Inicio', to: '/dashboard', action: 'navigate' },
      { icon: Bell, label: 'Alertas', action: 'notifications' },
      { icon: User, label: 'Perfil', to: '/settings', action: 'navigate' },
    ];
  }

  const handlePress = (item) => {
    if (item.action === 'navigate') {
      navigate(item.to);
    } else if (item.action === 'notifications') {
      setShowNotifications(true);
    } else if (item.action === 'tab') {
      // Dispatch a custom event that ClassView listens to
      window.dispatchEvent(new CustomEvent('bottom-nav-tab', { detail: item.tab }));
    }
  };

  const isActive = (item) => {
    if (item.action === 'navigate') {
      if (item.to === '/dashboard' && !isInClass) return path === '/dashboard';
      if (item.to === '/settings') return path === '/settings';
      if (item.to === '/admin') return path === '/admin';
    }
    return false;
  };

  return (
    <>
      <nav
        className="fixed bottom-0 left-0 right-0 z-50 glass border-t border-slate-200/60 sm:hidden"
        style={{ paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 0.5rem)' }}
        data-testid="bottom-nav"
      >
        <div className="flex items-center justify-around h-14 px-1 max-w-lg mx-auto">
          {items.map((item) => {
            const Icon = item.icon;
            const active = isActive(item);
            return (
              <button
                key={item.label}
                onClick={() => handlePress(item)}
                className={`flex flex-col items-center justify-center gap-0.5 flex-1 py-1.5 mx-0.5 rounded-xl transition-all active:scale-95 ${
                  active
                    ? 'bg-blue-50 text-blue-600 shadow-sm'
                    : 'text-slate-400 active:text-slate-600'
                }`}
                data-testid={`bottom-nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                <Icon className={`w-5 h-5 ${active ? 'stroke-[2.5]' : ''}`} />
                <span className="text-[10px] font-bold leading-none">{item.label}</span>
              </button>
            );
          })}
        </div>
      </nav>

      {/* Notification Sheet */}
      <NotificationSheet
        open={showNotifications}
        onClose={() => setShowNotifications(false)}
        user={user}
      />
    </>
  );
}
